import React, { useCallback, useMemo, useRef } from 'react';
import { RenderDelta } from 'quill-delta-to-react';
import { EICMType, EICMSeverity } from '../../enums/icm-enum';
import { useTrackICMEvent, useBuildICMProperties } from '../../services/logging/hooks';
import { usePageContext } from '../../utils/page-context';

export const PARSING_ERROR = 'PARSING_ERROR';

export const useParseDeltaObject = () => {
  const pageName = usePageContext();
  const trackICMEvent = useTrackICMEvent();
  const hasErrorBeenReported = useRef(false);
  const buildICMProperties = useBuildICMProperties();

  return useCallback(
    (message: string) => {
      if (!message || message.length === 0) {
        return undefined;
      }
      let delta;
      try {
        delta = JSON.parse(message);
        if (!delta.ops) {
          throw new Error('Invalid Delta object');
        }
      } catch (e: unknown) {
        if (!hasErrorBeenReported.current) {
          const icmProps = buildICMProperties(EICMType.General, EICMSeverity.Sev4, undefined, e as Error);
          trackICMEvent(`Unable to parse notification message for ${message}`, pageName, '', icmProps);
          hasErrorBeenReported.current = true;
        }
        delta = {
          isError: true,
          message: `${PARSING_ERROR}: ${message}`,
        };
      }
      return delta;
    },
    [buildICMProperties, pageName, trackICMEvent]
  );
};

export const useParseDeltaObjectTextOnlyCapped = (maxLenght: number) => {
  const parseDeltaObject = useParseDeltaObject();
  return useCallback(
    (message: string) => {
      const delta = parseDeltaObject(message);
      if (!delta || delta.isError) {
        return `${delta ? delta.message : ''}`;
      }
      const text = delta.ops.map((op: { insert: string }) => op.insert).join('');
      return text.length > maxLenght ? `${text.substring(0, maxLenght)}...` : text;
    },
    [maxLenght, parseDeltaObject]
  );
};

interface IProps {
  delta: string;
}

const RichTextRender = (props: IProps) => {
  const { delta } = props;
  const parseDeltaObject = useParseDeltaObject();
  const content = useMemo(() => parseDeltaObject(delta), [delta, parseDeltaObject]);
  const ops = content?.ops;
  return (
    ops &&
    ops.length > 0 && (
      <RenderDelta
        ops={ops}
        options={{
          paragraphTag: 'div',
          linkTarget: '_blank',
        }}
      />
    )
  );
};

export default RichTextRender;
